import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { useContent } from "../context/ContentContext";

export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} className={className}>
    {children}
  </motion.div>
);

export const GuideHero = ({ eyebrow, title, subtitle, image, primary, secondary, aside, testid }) => (
  <section className="relative overflow-hidden bg-jannah-green-dark" data-testid={testid}>
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 hero-overlay" />
    <div className="absolute inset-0 pattern-overlay opacity-20" />
    <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <motion.span initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="eyebrow text-jannah-gold-light">{eyebrow}</motion.span>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08]">{title}</motion.h1>
        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">{subtitle}</motion.p>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mt-9 flex flex-wrap gap-4">
          {primary}
          {secondary}
        </motion.div>
      </div>
      {aside && <div className="lg:col-span-5">{aside}</div>}
    </div>
  </section>
);

export const GoldButton = ({ to, href, children, testid, onClick }) => {
  const cls = "inline-flex items-center gap-2 px-7 py-4 rounded-full btn-gold font-bold uppercase tracking-wider text-sm transition-colors";
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{children}</a>;
  if (onClick) return <button onClick={onClick} className={cls} data-testid={testid}>{children}</button>;
  return <Link to={to} className={cls} data-testid={testid}>{children}</Link>;
};

export const GhostButton = ({ to, href, children, testid }) => {
  const cls = "inline-flex items-center gap-2 px-7 py-4 rounded-md border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors";
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{children}</a>;
  return <Link to={to} className={cls} data-testid={testid}>{children}</Link>;
};

export const FAQ = ({ eyebrow, title, items, testid }) => (
  <section className="section-pad bg-white" data-testid={testid}>
    <div className="max-w-4xl mx-auto px-6">
      <Reveal className="text-center mb-12">
        <span className="eyebrow text-jannah-green">{eyebrow}</span>
        <div className="gold-divider mx-auto mt-3" />
        <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{title}</h2>
      </Reveal>
      <Reveal i={1}>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="border border-jannah-sand rounded-xl px-6 bg-jannah-cream/60 data-[state=open]:bg-white data-[state=open]:shadow-lg transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-jannah-ink hover:text-jannah-green hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-jannah-ink/75 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const GuideCTA = ({ title, sub, btnLabel, btnTo, image, testid }) => {
  const { CONTACT } = useContent();
  return (
  <section className="relative overflow-hidden" data-testid={testid}>
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 bg-jannah-green-dark/85" />
    <div className="absolute inset-0 pattern-overlay opacity-25" />
    <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
      <Reveal>
        <h2 className="text-4xl md:text-5xl font-bold text-white">{title}</h2>
        <p className="mt-5 text-white/80 text-lg max-w-2xl mx-auto">{sub}</p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <GoldButton to={btnTo} testid="guide-cta-primary">{btnLabel} <ArrowRight size={17} /></GoldButton>
          <GhostButton href={CONTACT.whatsapp} testid="guide-cta-whatsapp"><MessageCircle size={17} /> WhatsApp {CONTACT.phone2}</GhostButton>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
